import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCalendarEvents } from "@/components/bookings/useCalendarEvents";
import { sourceStyle, ymd, addDays, mondayOf, lastDay, EventRow, SourceLegend } from "@/components/bookings/calendarUtils";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function MonthCalendar() {
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selected, setSelected] = useState(ymd(new Date()));
  const gridStart = mondayOf(month);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const { events, loading } = useCalendarEvents(ymd(days[0]), ymd(days[41]));
  const on = (d) => events.filter((e) => e.start <= d && lastDay(e) >= d);
  const today = ymd(new Date());
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));

  return (
    <div className="space-y-5" data-testid="month-calendar">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} data-testid="month-prev" className="p-2 rounded-md border border-border hover:bg-secondary"><ChevronLeft className="w-4 h-4" /></button>
          <h2 className="font-serif text-xl text-foreground min-w-[180px] text-center" data-testid="month-label">{month.toLocaleString(undefined, { month: "long", year: "numeric" })}</h2>
          <button onClick={() => shift(1)} data-testid="month-next" className="p-2 rounded-md border border-border hover:bg-secondary"><ChevronRight className="w-4 h-4" /></button>
          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>
        <SourceLegend />
      </div>
      <div className="grid grid-cols-7 border-l border-t border-border rounded-md overflow-hidden bg-card">
        {WEEKDAYS.map((w) => <div key={w} className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-2 border-r border-b border-border bg-secondary/40">{w}</div>)}
        {days.map((d) => {
          const key = ymd(d);
          const evs = on(key);
          const inMonth = d.getMonth() === month.getMonth();
          return (
            <button key={key} onClick={() => setSelected(key)} data-testid={`cal-day-${key}`}
              className={`min-h-[92px] text-left p-1.5 border-r border-b border-border transition-colors hover:bg-secondary/60 ${selected === key ? "bg-secondary" : ""} ${inMonth ? "" : "opacity-40"}`}>
              <span className={`text-xs inline-flex w-6 h-6 items-center justify-center rounded-full ${key === today ? "bg-primary text-primary-foreground" : "text-foreground"}`}>{d.getDate()}</span>
              <div className="space-y-0.5 mt-1">
                {evs.slice(0, 3).map((e) => (
                  <div key={e.id} className="text-[10px] leading-tight truncate rounded px-1 py-0.5 text-white" style={{ backgroundColor: sourceStyle(e.source).color }}>{e.time ? `${e.time} ` : ""}{e.guest || e.title}</div>
                ))}
                {evs.length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{evs.length - 3} more</div>}
              </div>
            </button>
          );
        })}
      </div>
      <div data-testid="selected-day-list">
        <h3 className="font-serif text-lg text-foreground mb-2">{new Date(`${selected}T00:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</h3>
        {on(selected).length === 0 ? <p className="text-sm text-muted-foreground">Nothing booked on this day.</p> : (
          <div className="bg-card border border-border rounded-md divide-y divide-border">{on(selected).map((e) => <EventRow key={e.id} e={e} />)}</div>
        )}
      </div>
    </div>
  );
}
